import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import "chart.js/auto";

function formatMMSS(seconds) {
  if (isNaN(seconds)) return "";
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}:${rs < 10 ? "0" : ""}${rs}`;
}

// Glow/shadow for the main line to achieve a "liquid glass" feel
const makeGlowPlugin = (glowEnabledRef) => ({
  id: "glow",
  beforeDatasetDraw(chart, args) {
    const { ctx } = chart;
    const datasetIndex = args.index;
    if (datasetIndex === 0 && glowEnabledRef.current) {
      ctx.save();
      ctx.shadowColor = "rgba(75, 192, 192, 0.35)";
      ctx.shadowBlur = 12;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    } else {
      ctx.save();
      ctx.shadowBlur = 0;
    }
  },
  afterDatasetDraw(chart) {
    chart.ctx.restore();
  },
});

// Gradient fill for Power dataset
const powerFillGradient = (context) => {
  const chart = context.chart;
  const { ctx, chartArea } = chart;
  if (!chartArea) return "rgba(75, 192, 192, 0.10)";
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, "rgba(75, 192, 192, 0.30)");
  gradient.addColorStop(1, "rgba(75, 192, 192, 0.05)");
  return gradient;
};

export default function TrainingLiveChart({
  ftp,
  targetSecWatt,
  power,
  cadence,
  bpm,
  totTime,
  height = 420,
}) {
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const countRef = useRef(0);
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);
  const glowEnabledRef = useRef(true);
  const isChartVisibleRef = useRef(true);
  const lastRenderRef = useRef(0);
  const scrollTimeoutRef = useRef(null);
  const maxTimeRef = useRef(0);

  // Initialize chart on component mount for free ride mode
  useEffect(() => {
    const initialLength = 61; // 60 seconds + 1 for initial
    const labels = Array.from({ length: initialLength }, (_x, i) => i);

    setChartData({
      labels,
      datasets: [
        {
          label: "Puissance (Watts)",
          borderWidth: 3,
          fill: true,
          showLine: true,
          tension: 0.35,
          cubicInterpolationMode: "monotone",
          pointRadius: 0,
          pointHoverRadius: 0,
          data: Array.from({ length: labels.length }, () => null),
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: powerFillGradient,
          borderCapStyle: "round",
          borderJoinStyle: "round",
        },
        {
          label: "Cadence (RPM)",
          borderWidth: 2.5,
          fill: false,
          showLine: true,
          tension: 0.3,
          cubicInterpolationMode: "monotone",
          pointRadius: 0,
          pointHoverRadius: 0,
          data: Array.from({ length: labels.length }, () => null),
          borderColor: "rgba(150, 150, 150, 1)",
          backgroundColor: "rgba(150, 150, 150, 0.15)",
        },
        {
          label: "Target Power (ERG) (Watts)",
          borderWidth: 2.5,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          stepped: "before",
          showLine: false, // Initially hide target line
          data: Array.from({ length: labels.length }, () => null),
          borderColor: "rgba(0,0,0,0)", // segment-colored
          segment: {
            borderColor: (ctx) => {
              const y = ctx.p0?.parsed?.y ?? ctx.p1?.parsed?.y ?? 0;
              if (y >= ftp * 1.5) {
                return "rgba(128, 0, 128, 1)";
              } else if (y >= ftp * 1.19) {
                return "rgba(255, 0, 0, 1)";
              } else if (y >= ftp * 1.05) {
                return "rgba(255, 165, 0, 1)";
              } else if (y >= ftp * 0.9) {
                return "rgba(255, 215, 0, 1)";
              } else if (y >= ftp * 0.75) {
                return "rgba(0, 128, 0, 1)";
              } else if (y > ftp * 0.6) {
                return "rgba(153, 204, 255, 1)";
              }
              return "rgba(128, 128, 128, 1)";
            },
          },
        },
        {
          label: "Heart Rate (BPM)",
          borderWidth: 2.5,
          fill: false,
          showLine: true,
          tension: 0.3,
          cubicInterpolationMode: "monotone",
          pointRadius: 0,
          pointHoverRadius: 0,
          data: Array.from({ length: labels.length }, () => null),
          borderColor: "rgba(200, 100, 100, 1)",
          backgroundColor: "rgba(200, 100, 100, 0.15)",
        },
      ],
    });
  }, [ftp]); // Include ftp since it's used in the segment function

  // Update chart when targets are added (but don't reset when they're removed)
  useEffect(() => {
    const watt = targetSecWatt || [];
    if (watt.length === 0) return; // Don't do anything when targets are removed

    // Expand chart to accommodate targets if needed
    setChartData((prev) => {
      const targetLength = watt.length + 1;
      const currentLength = prev.labels.length;
      const needsExpansion = targetLength > currentLength;

      const newLabels = needsExpansion
        ? [...prev.labels, ...Array.from({ length: targetLength - currentLength }, (_x, i) => currentLength + i)]
        : prev.labels;

      const newDatasets = prev.datasets.map((dataset, i) => {
        if (i === 2) { // Target dataset
          const newData = needsExpansion
            ? [...dataset.data, ...Array.from({ length: targetLength - currentLength }, () => null)]
            : dataset.data;
          // Set target data
          newData[0] = null;
          for (let j = 0; j < watt.length; j++) {
            newData[j + 1] = watt[j];
          }
          return {
            ...dataset,
            showLine: true,
            data: newData,
          };
        } else {
          // Expand other datasets with nulls if needed
          const newData = needsExpansion
            ? [...dataset.data, ...Array.from({ length: targetLength - currentLength }, () => null)]
            : dataset.data;
          return { ...dataset, data: newData };
        }
      });

      return {
        labels: newLabels,
        datasets: newDatasets,
      };
    });

    maxTimeRef.current = Math.max(maxTimeRef.current, watt.length);
  }, [targetSecWatt, ftp]); // Both targetSecWatt and ftp can affect the chart

  // Observe visibility of the chart to pause updates when offscreen
  useEffect(() => {
    if (!chartContainerRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio > 0;
        isChartVisibleRef.current = visible;
      },
      { threshold: [0, 0.01, 0.1, 0.5, 1] }
    );
    io.observe(chartContainerRef.current);
    return () => io.disconnect();
  }, []);

  // Temporarily disable glow while scrolling to reduce paint cost
  useEffect(() => {
    const onScroll = () => {
      glowEnabledRef.current = false;
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        glowEnabledRef.current = true;
        try {
          if (chartRef.current && chartRef.current.update) {
            chartRef.current.update("none");
          }
        } catch { }
      }, 200);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Incrementally write live samples per elapsed second
  useEffect(() => {
    if (!isChartVisibleRef.current) return;
    const now = Date.now();
    if (now - lastRenderRef.current < 250) return; // throttle
    lastRenderRef.current = now;
    // Write one point at index = totTime, expand if necessary
    const idx = totTime;
    setChartData((prev) => {
      if (!prev.datasets?.length) return prev;
      if (idx <= 0) return prev;

      const currentLength = prev.labels.length;
      let newLabels = prev.labels;
      let expanded = false;

      // Expand labels if needed
      if (idx >= currentLength) {
        const additionalLabels = Array.from({ length: idx - currentLength + 1 }, (_x, i) => currentLength + i);
        newLabels = [...prev.labels, ...additionalLabels];
        expanded = true;
        maxTimeRef.current = Math.max(maxTimeRef.current, idx);
      }

      const writeOrExpand = (arr, value) => {
        const next = arr.slice();
        if (expanded && next.length < newLabels.length) {
          // Expand array with nulls
          while (next.length < newLabels.length) {
            next.push(null);
          }
        }
        next[idx] = value ?? 0;
        return next;
      };

      return {
        ...prev,
        labels: newLabels,
        datasets: prev.datasets.map((d, i) => {
          if (i === 0) return { ...d, data: writeOrExpand(d.data, power) };
          if (i === 1) return { ...d, data: writeOrExpand(d.data, cadence) };
          if (i === 3) return { ...d, data: writeOrExpand(d.data, bpm) };
          // For target data, expand with nulls if needed
          if (i === 2 && expanded) {
            const next = d.data.slice();
            while (next.length < newLabels.length) {
              next.push(null);
            }
            return { ...d, data: next };
          }
          return d; // keep target static
        }),
      };
    });
    if (chartRef.current?.update) {
      try {
        chartRef.current.update("none");
      } catch { }
    }
    countRef.current = Math.max(countRef.current, idx + 1);
  }, [totTime, power, cadence, bpm]);

  const options = useMemo(
    () => ({
      responsive: true,
      animation: false,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: "index", axis: "x" },
      devicePixelRatio: Math.min(
        1.5,
        typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1
      ),
      elements: {
        point: { radius: 0, hoverRadius: 0 },
        line: {
          borderCapStyle: "round",
          borderJoinStyle: "round",
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
          align: "start",
          labels: {
            usePointStyle: true,
            boxWidth: 10,
            padding: 6,
          },
        },
        decimation: {
          enabled: true,
          algorithm: "min-max",
          samples: 600,
        },
      },
      layout: {
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
      },
      scales: {
        x: {
          grid: {
            display: true,
            color: "rgba(0,0,0,0.10)", // light grey vertical grid lines
            borderColor: "rgba(0,0,0,0.12)",
            borderDash: [3, 3],
            lineWidth: 1,
          },
          ticks: {
            callback: function (value) {
              const label = this.getLabelForValue(value);
              const seconds = Number(label);
              return formatMMSS(seconds);
            },
          },
          title: {
            display: true,
            text: "Time (mm:ss)",
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(75, 192, 192, 0.18)", // colored horizontal grid lines
            borderColor: "rgba(75, 192, 192, 0.28)",
          },
          title: {
            display: true,
            text: "Watts x RPM x BPM",
          },
        },
      },
    }),
    []
  );

  return (
    <div
      ref={chartContainerRef}
      style={{ height: height, contain: "layout paint size" }}
      className=""
    >
      <Line ref={chartRef} data={chartData} options={options} plugins={[makeGlowPlugin(glowEnabledRef)]} />
    </div>
  );
}

